import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import useActivityView from '@/data/hook/useActivityView';
import { updateDndPositionalColumnProgress } from '@/data/services/dndColumnProgressServices';
import { ActivityMode } from '@/models/ActivityProgress';
import { DNDColumn } from '@/models/DNDColumn';
import DNDColumnProgress from '@/models/DNDColumnProgress';
import DNDItemProgress from '@/models/DNDItemProgress';
import alert from '@/utils/shared/alert';
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnswerDebounce } from '../../ActivitiesPreview.hooks';
import DNDPositionalItemProgress from './DNDPositionalItemProgress';

interface DNDPositionalColumnProgressProps {
  dndPositionalColumnProgress: DNDColumnProgress;
  activityMode: ActivityMode;
  setHasChange(hasChange: boolean): Promise<void> | void;
  columnProgress?: DNDColumnProgress;
  answerColumn?: DNDColumn;
}

type UpdateAnswerArgs = {
  items: DNDItemProgress[];
  itemsSource: DNDItemProgress[];
};

export default function DNDPositionalColumnProgress({
  dndPositionalColumnProgress,
  activityMode,
  setHasChange,
  columnProgress,
  answerColumn,
}: DNDPositionalColumnProgressProps) {
  const { column, itemsProgress, id } = dndPositionalColumnProgress;
  const { blockStepNavigate } = useActivityView();

  const { t } = useTranslation('translation', {
    keyPrefix: 'lesson.activity.dragAndDropPositional',
  });

  const isDisabled = activityMode !== 'activity';

  const [itemsState, setItemsState] =
    useState<DNDItemProgress[]>(itemsProgress);

  const { debounce } = useAnswerDebounce();

  const updateAnswer = async ({ items }: UpdateAnswerArgs) => {
    await updateDndPositionalColumnProgress(id, items);
    await setHasChange(true);
  };

  const { mutate: update } = useMutation(updateAnswer, {
    onMutate() {
      blockStepNavigate(true);
    },
    onError(_, variables) {
      setItemsState(variables.itemsSource);
      alert.error(t('errorReordenation'));
    },
    onSettled() {
      blockStepNavigate(false);
    },
  });

  async function handleOnDragEnd(result: DropResult) {
    if (!result.destination) return;

    if (itemsState) {
      const itemsSource = [...itemsState];
      const items = itemsState;
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setItemsState(items);
      debounce(() => update({ items, itemsSource }));
    }
  }

  useEffect(() => {
    setItemsState(itemsProgress);
  }, [itemsProgress]);

  return (
    <div className="flex flex-col gap-3 items-center w-full">
      <ConditionalRenderer condition={!!column.name}>
        <Text format="rubik-500" className="text-accent" text={column.name} />
      </ConditionalRenderer>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable
          droppableId="dndPositionalItemProgressList"
          isDropDisabled={isDisabled}
        >
          {provided => (
            <div
              className="flex flex-col justify-around w-full gap-3"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {itemsState.map(
                (dndPositionalItemProgress: DNDItemProgress, index) => {
                  if (columnProgress) {
                    const findDndItemProgress =
                      columnProgress.itemsProgress.find(
                        itemProgress =>
                          itemProgress.id === dndPositionalItemProgress.id,
                      );

                    if (findDndItemProgress) {
                      const findAnswer = answerColumn?.items.find(
                        ({ id }) => id === findDndItemProgress.item.id,
                      );
                      return (
                        <DNDPositionalItemProgress
                          index={index}
                          key={findDndItemProgress.id}
                          dndPositionalItemProgress={findDndItemProgress}
                          activityMode={activityMode}
                          isDisabled={isDisabled}
                          answerOrder={findAnswer?.order}
                        />
                      );
                    } else return <React.Fragment />;
                  } else {
                    return (
                      <DNDPositionalItemProgress
                        index={index}
                        key={dndPositionalItemProgress.id}
                        dndPositionalItemProgress={dndPositionalItemProgress}
                        activityMode={activityMode}
                        isDisabled={isDisabled}
                      />
                    );
                  }
                },
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
